import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/api';
import type { User } from '@/shared/types/user';
import { createSafeContext } from '@/shared/utils/create-safe-context';
import { setSentryUser } from '@/shared/utils/sentry-user-context';

type UserContextValue = {
  user: Omit<User, 'is_confirmed'> | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
};

const [UserProvider, useUser] = createSafeContext<UserContextValue>('UserContext');

function UserContextProvider({ children }: Readonly<{ children: React.ReactNode }>) {
  const { data, isLoading, error, refetch } = useQuery<Omit<User, 'is_confirmed'>, Error>({
    queryKey: ['user'],
    queryFn: async () => await api.get<Omit<User, 'is_confirmed'>>('users/me'),
    retry: false,
    refetchOnWindowFocus: false,
  });

  // Sentry에 사용자 정보 설정
  // TanStack Query의 data가 변경될 때마다 Sentry 사용자 정보 업데이트
  useEffect(() => {
    // 로딩 중이 아니고 데이터가 있을 때만 설정
    if (!isLoading) {
      if (data) {
        setSentryUser(data);
      } else {
        // 데이터가 없거나 에러가 발생한 경우 사용자 정보 초기화
        setSentryUser(null);
      }
    }
  }, [data, isLoading]);

  const value: UserContextValue = {
    user: data ?? null,
    isLoading,
    error,
    refetch,
  };

  return <UserProvider value={value}>{children}</UserProvider>;
}

export { UserContextProvider, useUser };
